import React from 'react';
import { Box, Typography, Stack, Chip } from '@mui/material';

export const prettyResourceType = (value?: string | null) => {
    const key = String(value || '').toUpperCase();
    switch (key) {
        case 'BATCH_ADD_RESOURCE': return 'Add resources';
        case 'ADD_RESOURCE': return 'Add resource';
        case 'ACTIVATE': return 'Activation';
        case 'RENEW': return 'Renewal';
        case 'UPGRADE': return 'Upgrade';
        case 'ADD_CHANNEL':
        case 'CHANNEL': return 'Channel';
        case 'SOCIAL_STREAM': return 'Social Stream';
        case 'PLAYKASTER':
        case 'PLAYKASTR': return 'PlayKaster';
        case 'HLS_STANDARD': return 'HLS';
        case 'HLS_SCTE35':
        case 'HLS_PLUS': return 'HLS+';
        case 'DIRECT_OUTPUT':
        case 'EXTRA_PASSTHROUGH': return 'Direct Pass';
        case 'MANAGED_OUTPUT':
        case 'EXTRA_TRANSCODED':
        case 'FULL_TRANSCODED': return 'Remapped Output';
        case 'MANAGED_UPGRADE':
        case 'UPGRADE_INCLUDED':
        case 'UPGRADE_INCLUDED_SLOT':
        case 'MANAGED_SERVICE_UPGRADE': return 'Remap Upgrade';
        case 'EPG_XMLTV': return 'XMLTV EPG';
        case 'EPG_GRACENOTE': return 'Gracenote EPG';
        case 'MANUAL': return 'Manual Admin';
        case 'ADMIN_LEDGER_ENTRY': return 'Admin Ledger Entry';
        default:
            return key ? key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()) : 'Other';
    }
};

export const formatTransactionDescription = (rawDesc: string): React.ReactNode => {
    // 1. Simple text fallback
    if (!rawDesc || !rawDesc.trim().startsWith('{')) {
        return <Typography variant="body2">{rawDesc || 'General Transaction'}</Typography>;
    }

    try {
        const data = JSON.parse(rawDesc);
        
        // 2. Renewal / Activation
        if (data.action === 'RENEW' || data.action === 'ACTIVATE') {
            return (
                <Box>
                    <Typography variant="body2" fontWeight={600}>
                        {data.targetPlan ? 
                            `${data.targetPlan.charAt(0).toUpperCase() + data.targetPlan.slice(1).toLowerCase()} Subscription` 
                            : 'Plan Renewal'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {data.action === 'ACTIVATE' ? 'New Activation' : 'Monthly Recurring'}
                    </Typography>
                </Box>
            );
        }

        // 3. Add-ons (Batch Resources)
        if (data.action === 'BATCH_ADD_RESOURCE' || data.action === 'ADD_RESOURCE') {
            const items = data.items || (data.resourceType ? [data] : []);
            
            return (
                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ color: 'primary.main' }}>
                        Resource Expansion
                    </Typography>
                    <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
                        {items.map((item: any, idx: number) => {
                            const label = prettyResourceType(item.resourceType);
                            
                            return (
                                <Chip 
                                    key={idx}
                                    label={`+${item.quantity} ${label}`} 
                                    size="small" 
                                    variant="outlined" 
                                    sx={{ 
                                        height: 20, 
                                        fontSize: '0.65rem', 
                                        borderColor: 'divider',
                                        bgcolor: 'action.hover'
                                    }} 
                                />
                            );
                        })}
                    </Stack>
                </Box>
            );
        }

        // Fallback for upgrade actions
        if (data.action === 'UPGRADE') {
             return <Typography variant="body2" fontWeight={600}>Plan Upgrade</Typography>;
        }

        return <Typography variant="body2" fontWeight={600}>{prettyResourceType(data.action)}</Typography>;

    } catch (e) {
        return <Typography variant="body2">{rawDesc}</Typography>;
    }
};
